import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { AXIOS_INSTANCE, customInstance } from '../../services/api-client'
import type {
  AgentReview,
  CloudAccrualConfig,
  CloudAccrualConfigUpdate,
  CloudAccrualConnectorOption,
  CloudAccrualHealth,
  CloudAccrualRun,
} from './types'

const BASE = '/api/cloud-accruals'

export const cloudAccrualKeys = {
  all: ['cloud-accruals'] as const,
  run: () => [...cloudAccrualKeys.all, 'run'] as const,
  health: () => [...cloudAccrualKeys.all, 'health'] as const,
  config: () => [...cloudAccrualKeys.all, 'config'] as const,
  connectors: () => [...cloudAccrualKeys.all, 'connectors'] as const,
  agentReview: () => [...cloudAccrualKeys.all, 'agent-review'] as const,
}

export function useCloudAccrualRun(options?: { enabled?: boolean }) {
  return useQuery<CloudAccrualRun>({
    queryKey: cloudAccrualKeys.run(),
    queryFn: () => customInstance({ url: `${BASE}/run`, method: 'GET' }),
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  })
}

export function useCloudAccrualHealth() {
  return useQuery<CloudAccrualHealth>({
    queryKey: cloudAccrualKeys.health(),
    queryFn: () => customInstance({ url: `${BASE}/health`, method: 'GET' }),
    staleTime: 60_000,
    retry: 1,
  })
}

export function useCloudAccrualDataSourceConfig() {
  return useQuery<CloudAccrualConfig>({
    queryKey: cloudAccrualKeys.config(),
    queryFn: () => customInstance({ url: `${BASE}/config/data-source`, method: 'GET' }),
    staleTime: 60_000,
  })
}

export function useCloudAccrualAvailableConnectors() {
  return useQuery<CloudAccrualConnectorOption[]>({
    queryKey: cloudAccrualKeys.connectors(),
    queryFn: () => customInstance({ url: `${BASE}/config/available-connectors`, method: 'GET' }),
    staleTime: 60_000,
  })
}

export function useSetCloudAccrualDataSource() {
  const queryClient = useQueryClient()
  return useMutation<CloudAccrualConfig, Error, CloudAccrualConfigUpdate>({
    mutationFn: payload =>
      customInstance({
        url: `${BASE}/config/data-source`,
        method: 'PUT',
        data: payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cloudAccrualKeys.all })
    },
  })
}

export function useClearCloudAccrualDataSource() {
  const queryClient = useQueryClient()
  return useMutation<CloudAccrualConfig, Error, void>({
    mutationFn: () =>
      customInstance({
        url: `${BASE}/config/data-source`,
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cloudAccrualKeys.all })
    },
  })
}

export function useTestCloudAccrualConnection() {
  return useMutation<CloudAccrualHealth, Error, CloudAccrualConfigUpdate>({
    mutationFn: payload =>
      customInstance({
        url: `${BASE}/config/test-connection`,
        method: 'POST',
        data: payload,
      }),
  })
}

export function useCloudAccrualAgentReview() {
  return useMutation<AgentReview, Error, { use_llm?: boolean }>({
    mutationKey: cloudAccrualKeys.agentReview(),
    mutationFn: body =>
      customInstance({
        url: `${BASE}/agent/review`,
        method: 'POST',
        data: { use_llm: body.use_llm ?? false },
      }),
  })
}

export async function downloadCloudAccrualExport(exportType: 'je' | 'reversal') {
  const response = await AXIOS_INSTANCE.get(`${BASE}/exports/${exportType}`, {
    responseType: 'blob',
  })
  const disposition = response.headers['content-disposition'] || ''
  const match = disposition.match(/filename="([^"]+)"/)
  const filename =
    match?.[1] || (exportType === 'je'
      ? 'Cloud_Accruals_JE_Load_Apr2025.csv'
      : 'Cloud_Accruals_Reversal_Load_May2025.csv')
  const url = window.URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export async function openCloudAccrualReport() {
  const response = await AXIOS_INSTANCE.get(`${BASE}/report-html`, {
    responseType: 'blob',
  })
  const url = window.URL.createObjectURL(response.data)
  window.open(url, '_blank', 'noopener,noreferrer')
  window.setTimeout(() => window.URL.revokeObjectURL(url), 30_000)
}
